// web/watch.ts — WATCH MODE: the "leave it running on a second monitor" layer.
//
// Pure PRESENTATION over the live sim (no sim/determinism/save impact) — it makes
// BROOD pleasant to just watch:
//   • a CINEMATIC CAMERA with modes (Fit / Follow the nest / auto Tour) that smoothly
//     zooms and pans between points of interest;
//   • INERTIAL MOVEMENT — the sim is discrete whole-tile steps; this gives each
//     organism a VISUAL position + velocity that chases its sim tile with
//     momentum (a critically-damped spring: it accelerates into a gap and eases
//     to a stop on arrival, never snapping) and a HEADING that turns smoothly
//     toward travel — per-caste, so Workers read decisive, Soldiers heavy, Flyers
//     glide. Colonies look alive instead of teleporting between cells;
//   • SPECIES OBSERVATION — spotlight one caste (others dim) for a documentary feel.
//
// render.ts asks this for the ViewBox + per-organism interpolated positions; main.ts
// advances it each frame. No Math.random in a way that matters (a little camera
// drift jitter is fine — this never touches the simulation).

import { KIND } from '../src/types.ts';
import type { GameSession } from './game.ts';
import type { ViewBox } from './render.ts';

export type CameraMode = 'manual' | 'fit' | 'follow' | 'cinematic';
// Manual leads the cycle: the player-driven camera (pan/zoom + fog exploration) is
// the default play view; Fit is the explicit "show me the whole map" toggle.
export const CAMERA_MODES: readonly CameraMode[] = ['manual', 'fit', 'follow', 'cinematic'];
export const CAMERA_LABEL: Record<CameraMode, string> = {
  manual: 'Explore', fit: 'Fit', follow: 'Follow nest', cinematic: 'Cinematic',
};

// --- PLAYER CAMERA: zoom range + helpers (pure, exported for tests) -----------
// Zoom is a multiplier over the FIT cell (the cell size that shows the whole map).
// 1 = whole map; MAX_ZOOM = closest the player can pull in. Min is computed as the
// zoom that exactly fits the smaller axis, so the player can always "see all" but
// never zoom further out than the world.
export const MAX_ZOOM = 6;
/** Tiles visible across the viewport's smaller axis when the camera first opens. */
export const DEFAULT_VIEW_TILES = 32;
/** Fraction of a viewport allowed past each world edge (so an edge nest can still center-ish). */
const EDGE_MARGIN = 0.5;

/** The fit cell: the square cell size (px) that shows the WHOLE grid in the canvas. */
export function baseFitCell(cssW: number, cssH: number, gridW: number, gridH: number): number {
  return Math.min(cssW / Math.max(1, gridW), cssH / Math.max(1, gridH));
}

/**
 * Lowest zoom multiplier the camera allows — the whole map fit. `baseFitCell`
 * already fits both axes, so min-zoom is 1 (zooming below 1 would shrink the map
 * smaller than the canvas, which the player never needs).
 */
export function minZoom(): number {
  return 1;
}

/** Clamp a zoom multiplier to [minZoom, MAX_ZOOM]. */
export function clampZoom(zoom: number): number {
  const lo = minZoom();
  if (!Number.isFinite(zoom)) return lo;
  return Math.min(MAX_ZOOM, Math.max(lo, zoom));
}

/**
 * Per-move zoom factor for a two-finger pinch: the ratio of the current finger distance
 * to the previous one, clamped to [0.5, 2] so a single jittery frame can't snap the zoom.
 * A degenerate previous/current distance returns 1 (no change). Pure — the caller applies
 * it via `zoomBy` (which re-clamps to the zoom range). Unit-tested in camera.test.mjs.
 */
export function pinchZoomFactor(prevDist: number, curDist: number): number {
  if (!(prevDist > 0) || !(curDist > 0)) return 1;
  const f = curDist / prevDist;
  return Math.min(2, Math.max(0.5, f));
}

/**
 * Clamp the camera center (tile-space) so the viewport stays within the world,
 * with a half-viewport margin so an edge nest can still sit near the middle. When
 * the world is smaller than the viewport on an axis (zoomed all the way out), the
 * center is pinned to the world's midpoint on that axis. Pure + deterministic.
 */
export function clampCamera(
  cx: number, cy: number, zoom: number,
  cssW: number, cssH: number, gridW: number, gridH: number, fitCell: number,
): { cx: number; cy: number } {
  const cell = Math.max(2, fitCell * zoom);
  const halfW = cssW / (2 * cell); // half-viewport in tiles
  const halfH = cssH / (2 * cell);
  return {
    cx: clampAxis(cx, gridW, halfW),
    cy: clampAxis(cy, gridH, halfH),
  };
}

function clampAxis(c: number, span: number, half: number): number {
  // If the viewport is wider than the world on this axis, lock to the midpoint.
  if (half >= span / 2) return span / 2;
  const margin = half * EDGE_MARGIN;
  const lo = half - margin;
  const hi = span - half + margin;
  if (c < lo) return lo;
  if (c > hi) return hi;
  return c;
}

/** Map a CSS-pixel point to a (fractional) tile coordinate for the given viewbox. */
export function screenToTile(cssX: number, cssY: number, view: ViewBox): { tx: number; ty: number } {
  return { tx: (cssX - view.offX) / view.cell, ty: (cssY - view.offY) / view.cell };
}

/** Map a (fractional) tile coordinate to a CSS-pixel point for the given viewbox. */
export function tileToScreen(tx: number, ty: number, view: ViewBox): { sx: number; sy: number } {
  return { sx: view.offX + tx * view.cell, sy: view.offY + ty * view.cell };
}

const SNAP_TILES = 4;         // per-axis jump larger than this ⇒ teleport (don't glide).
                              // The deliberate smoothing below lags ~2-3 tiles, so the
                              // snap bar sits above that — only a real respawn exceeds it.
const HEAD_MIN_SPEED2 = 0.04; // below this speed² (tiles/s)² keep the last facing (no spin at rest)
const CAM_SPEED = 2.6;        // camera easing toward its target
const FOLLOW_ZOOM = 2.6;
const CINE_ZOOM = 2.9;
const DWELL = 7.5;            // seconds the cinematic camera lingers per point of interest

// --- IDLE BEHAVIOUR (render-only) -------------------------------------------
const IDLE_DELAY = 0.5;       // seconds a sim tile must hold still before idle anims begin
const ORIENT_TURN = 0.5;      // idle: gently face the current target (turn time, seconds)
const SWAY_FREQ = 2.1;        // idle micro-sway angular speed (a slow breathing orbit)
const SWAY_EASE = 7;          // how fast the sway offset eases in/out (per second)
const GROOM_RATE = 0.09;      // grooming bouts per second (slow — occasional)
const GROOM_DUTY = 0.16;      // fraction of a cycle spent in a grooming bout
const GROOM_WIGGLE = 24;      // head-wiggle speed within a bout
const GROOM_AMP = 5;          // groom head-wiggle amplitude scale (× the caste sway)

// --- INERTIAL MOTION MODEL ---------------------------------------------------
// All render-only; never touches the sim, determinism, or any state hash.

/**
 * Per-caste motion feel. `pos` = position smoothing time in seconds (bigger =
 * heavier / laggier into and out of motion = more inertia); `turn` = heading
 * smoothing time in seconds (bigger = slower, wider turns = a strict turn-rate
 * limit that stops the body snapping around when the sim reverses a path); `sway`
 * = idle micro-movement amplitude (tiles) — the resting liveliness. Tuned heavier
 * than a snappy RTS so motion reads BIOLOGICAL: Workers purposeful, Soldiers
 * deliberate, Flyers glide. Indexed by CASTE.* (Worker, Soldier, Flyer, Specialist).
 */
export interface CasteMotion { readonly pos: number; readonly turn: number; readonly sway: number; }
export const CASTE_MOTION: readonly CasteMotion[] = [
  { pos: 0.17, turn: 0.16, sway: 0.05 }, // Worker     — purposeful: tracks fairly tight + eases to face travel; fidgets at rest
  { pos: 0.32, turn: 0.40, sway: 0.02 }, // Soldier    — deliberate: heavy inertia, slow ponderous wide turns; near-still on guard
  { pos: 0.30, turn: 0.60, sway: 0.06 }, // Flyer      — glide: floaty with the widest, slowest banking arcs; hovers/bobs at rest
  { pos: 0.22, turn: 0.22, sway: 0.04 }, // Specialist — a balanced middle
];
const DEFAULT_MOTION: CasteMotion = { pos: 0.24, turn: 0.24, sway: 0.04 };

// Non-colony movers (wildlife + the apex boss) share one light motion feel —
// they have no caste and no idle behaviours, just an inertial chase + smooth
// facing so they flow between tiles instead of teleporting. A touch snappier than
// the colony castes (predators dart) and a wider turn so big bodies bank, not
// snap. Render-only, keyed by the same entity id as everything else.
const WILDLIFE_MOTION: CasteMotion = { pos: 0.14, turn: 0.30, sway: 0 };

/** Wrap an angle into (-π, π]. */
export function wrapPi(a: number): number {
  const t = Math.PI * 2;
  a %= t;
  if (a > Math.PI) a -= t;
  else if (a <= -Math.PI) a += t;
  return a;
}

/**
 * Canvas facing angle for a velocity vector. Creatures are drawn nose-UP (local
 * −y), so `ctx.rotate(headingFromVel(vx,vy))` points the nose along (vx, vy).
 */
export function headingFromVel(vx: number, vy: number): number {
  return Math.atan2(vx, -vy);
}

/**
 * Ease `heading` toward `desired` along the SHORTEST arc, frame-rate independent
 * (exponential, time-constant `turn` seconds). Returns the new wrapped heading.
 */
export function turnToward(heading: number, desired: number, turn: number, dt: number): number {
  const diff = wrapPi(desired - heading);
  const k = 1 - Math.exp(-dt / Math.max(1e-4, turn));
  return wrapPi(heading + diff * k);
}

/**
 * An idle GROOMING head-wiggle: 0 for most of the cycle, with brief enveloped
 * bouts (a critter pausing to preen). Deterministic function of an organism's
 * idle `phase` (seconds, id-offset for variety) — render-only, no RNG. Returns a
 * heading offset in radians scaled by `amp`.
 */
export function groomOffset(phase: number, amp: number): number {
  const cyc = phase * GROOM_RATE;
  const frac = cyc - Math.floor(cyc);
  if (frac >= GROOM_DUTY) return 0;               // not in a grooming bout
  const env = Math.sin((frac / GROOM_DUTY) * Math.PI); // 0 → 1 → 0 across the bout
  return Math.sin(phase * GROOM_WIGGLE) * amp * env;
}

/**
 * Critically-damped smooth-damp of one axis toward `target`. Velocity is read +
 * written through `vel[vi]` so the hot loop allocates nothing. It accelerates as
 * a gap opens and decelerates to a clean stop on arrival (no overshoot/jitter).
 * Standard Game-Programming-Gems / Unity `SmoothDamp` form.
 */
export function smoothDamp(
  cur: number, target: number, vel: Float32Array, vi: number, smoothTime: number, dt: number,
): number {
  const st = Math.max(1e-4, smoothTime);
  const omega = 2 / st;
  const x = omega * dt;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  const change = cur - target;
  const temp = ((vel[vi] as number) + omega * change) * dt;
  vel[vi] = ((vel[vi] as number) - omega * temp) * exp;
  let out = target + (change + temp) * exp;
  // Overshoot guard: if we crossed the target this step, clamp and kill velocity.
  if ((target - cur > 0) === (out > target)) { out = target; vel[vi] = 0; }
  return out;
}

export class WatchView {
  readonly capacity: number;
  mode: CameraMode = 'fit';
  /** Observed caste (CASTE.*) for the spotlight, or -1 for none. */
  observedCaste = -1;

  // smoothed camera (tile-space center + zoom multiplier over the fit cell)
  private curZoom = 1;
  private curCx = 0;
  private curCy = 0;
  private inited = false;

  // MANUAL (player) camera target — driven by pan/zoom input, eased toward by the
  // shared smoother. Re-clamped against the live canvas/grid each frame so a resize
  // or zoom-out can never strand the viewport outside the world.
  private manZoom = 1;
  private manCx = 0;
  private manCy = 0;
  private manSet = false; // becomes true once the player camera has been positioned
  // Last canvas size seen by viewbox() — lets pan/zoom clamp + wheel-anchor math
  // work in CSS pixels without threading the size through every call site.
  private lastCssW = 0;
  private lastCssH = 0;

  // cinematic tour
  private tourTime = 0;
  private tourIdx = 0;

  // Inertial motion buffers (indexed by entity id). Visual position (rx,ry),
  // velocity (vx,vy, tiles/s), smoothed facing (head, radians) + whether it has
  // been set, and a per-frame `seen` stamp so a recycled id snaps instead of
  // gliding across the map from whoever held the slot before.
  private readonly rx: Float32Array;
  private readonly ry: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly head: Float32Array;
  private readonly hasHead: Uint8Array;
  private readonly hasPos: Uint8Array;
  private readonly seen: Int32Array;
  private frame = 0;

  // Idle-behaviour buffers (render-only). `stillT` = seconds the sim tile has held
  // still; `lsx/lsy` = the last sim tile seen (to detect a change); `idleT` = idle
  // phase clock; `swx/swy` = the eased idle position offset (tiles); `swh` = the
  // eased idle heading offset (groom wiggle), all 0 while the organism is moving.
  private readonly stillT: Float32Array;
  private readonly lsx: Int32Array;
  private readonly lsy: Int32Array;
  private readonly idleT: Float32Array;
  private readonly swx: Float32Array;
  private readonly swy: Float32Array;
  private readonly swh: Float32Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.rx = new Float32Array(capacity);
    this.ry = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.head = new Float32Array(capacity);
    this.hasHead = new Uint8Array(capacity);
    this.hasPos = new Uint8Array(capacity);
    this.seen = new Int32Array(capacity).fill(-1);
    this.stillT = new Float32Array(capacity);
    this.lsx = new Int32Array(capacity).fill(-1);
    this.lsy = new Int32Array(capacity).fill(-1);
    this.idleT = new Float32Array(capacity);
    this.swx = new Float32Array(capacity);
    this.swy = new Float32Array(capacity);
    this.swh = new Float32Array(capacity);
  }

  // --- controls --------------------------------------------------------------

  cycleMode(): void {
    const i = CAMERA_MODES.indexOf(this.mode);
    const next = CAMERA_MODES[(i + 1) % CAMERA_MODES.length] as CameraMode;
    this.tourTime = 0;
    if (next === 'manual') { this.setManual(); return; } // seeds target from live cam
    this.mode = next;
  }

  setMode(mode: CameraMode): void {
    this.mode = mode;
    this.tourTime = 0;
  }

  /** Cycle the observation spotlight: none → Worker → Soldier → Flyer → Specialist → none. */
  cycleObserved(): void {
    this.observedCaste = this.observedCaste >= 3 ? -1 : this.observedCaste + 1;
  }

  // --- PLAYER (manual) camera ------------------------------------------------
  // Any of these switches the camera to manual mode and seeds the manual target
  // from wherever the camera currently sits (so toggling out of Fit/Follow into a
  // pan/zoom doesn't jump). The shared smoother in driveCamera eases toward it.

  /** Switch to the player-driven camera, seeding its target from the live camera. */
  setManual(): void {
    if (!this.manSet) {
      this.manZoom = this.inited ? this.curZoom : 1;
      this.manCx = this.inited ? this.curCx : this.manCx;
      this.manCy = this.inited ? this.curCy : this.manCy;
      this.manSet = true;
    }
    this.mode = 'manual';
  }

  /**
   * Position the player camera explicitly: center on a tile at a given zoom
   * (default keep current). Used for the initial "spawn looking at your nest"
   * view and the recenter button. Switches to manual.
   */
  initManual(tileX: number, tileY: number, zoom?: number): void {
    this.manZoom = clampZoom(zoom ?? this.manZoom);
    this.manCx = tileX;
    this.manCy = tileY;
    this.manSet = true;
    this.mode = 'manual';
    // Snap the smoother so the opening frame is already on the nest (no glide-in
    // across the whole map from a stale center).
    this.curZoom = this.manZoom;
    this.curCx = this.manCx;
    this.curCy = this.manCy;
    this.inited = true;
  }

  /**
   * Zoom multiplier that makes `tiles` span the SMALLER canvas axis — used to open
   * the camera "~32 tiles across" rather than fit-all. `baseFitCell` fits the whole
   * grid; this returns how much to multiply it by to frame `tiles` instead.
   */
  zoomForTilesAcross(tiles: number, cssW: number, cssH: number, gridW: number, gridH: number): number {
    const fit = baseFitCell(cssW, cssH, gridW, gridH);
    if (fit <= 0 || tiles <= 0) return 1;
    const minAxisPx = Math.min(cssW, cssH);
    const wantCell = minAxisPx / tiles; // px per tile to show `tiles` across
    return clampZoom(wantCell / fit);
  }

  /** Recenter the player camera on a tile (keeps current zoom). Switches to manual. */
  centerOn(tileX: number, tileY: number): void {
    this.setManual();
    this.manCx = tileX;
    this.manCy = tileY;
  }

  /** Pan the player camera by a tile delta (e.g. from a drag). Switches to manual. */
  panBy(dxTiles: number, dyTiles: number): void {
    this.setManual();
    this.manCx += dxTiles;
    this.manCy += dyTiles;
  }

  /**
   * Zoom the player camera by `factor` (>1 in, <1 out). When a screen anchor +
   * canvas/grid are given, keeps the world point under the cursor fixed (wheel-to-
   * cursor); otherwise zooms about the view center. Switches to manual.
   */
  zoomBy(
    factor: number,
    anchorCssX?: number, anchorCssY?: number,
    cssW?: number, cssH?: number, gridW?: number, gridH?: number,
  ): void {
    this.setManual();
    const oldZoom = this.manZoom;
    const newZoom = clampZoom(oldZoom * factor);
    if (anchorCssX !== undefined && anchorCssY !== undefined &&
        cssW !== undefined && cssH !== undefined && gridW !== undefined && gridH !== undefined) {
      // World tile currently under the anchor, from the MANUAL target viewbox (not
      // the eased one) so repeated wheel ticks compound predictably.
      const fit = baseFitCell(cssW, cssH, gridW, gridH);
      const oldCell = Math.max(2, fit * oldZoom);
      const newCell = Math.max(2, fit * newZoom);
      const offXold = cssW / 2 - this.manCx * oldCell;
      const offYold = cssH / 2 - this.manCy * oldCell;
      const tileX = (anchorCssX - offXold) / oldCell;
      const tileY = (anchorCssY - offYold) / oldCell;
      // Solve for the new center that keeps (tileX,tileY) under the anchor.
      this.manCx = (anchorCssX - cssW / 2) / -newCell + tileX;
      this.manCy = (anchorCssY - cssH / 2) / -newCell + tileY;
    }
    this.manZoom = newZoom;
  }

  modeLabel(): string {
    return CAMERA_LABEL[this.mode];
  }

  // --- per-frame update ------------------------------------------------------

  update(session: GameSession, dt: number, gridW: number, gridH: number): void {
    if (dt <= 0) dt = 1 / 60;
    this.interpolate(session, dt);
    this.driveCamera(session, dt, gridW, gridH);
  }

  private interpolate(session: GameSession, dt: number): void {
    const e = session.world.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    this.frame++;
    const prev = this.frame - 1;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      const kind = e.kind[id] as number;
      // Smooth colony organisms (caste motion + idle behaviours) AND the
      // non-colony movers — wildlife and the apex boss — which otherwise teleport
      // between sim tiles. Everything else (food, walls) holds still, skip it.
      if (kind !== KIND.Organism && kind !== KIND.Wildlife && kind !== KIND.Apex) continue;
      const px = e.posX[id] as number;
      const py = e.posY[id] as number;

      // Snap (no glide) on first sight, on a recycled/returned id (not advanced
      // last frame), or on a real teleport — anything else would streak a line
      // across the map.
      const fresh = this.hasPos[id] === 0 || (this.seen[id] as number) !== prev;
      this.seen[id] = this.frame;
      if (fresh ||
          Math.abs(px - (this.rx[id] as number)) > SNAP_TILES ||
          Math.abs(py - (this.ry[id] as number)) > SNAP_TILES) {
        this.rx[id] = px; this.ry[id] = py;
        this.vx[id] = 0; this.vy[id] = 0;
        this.hasPos[id] = 1;
        if (this.hasHead[id] === 0) { this.head[id] = 0; this.hasHead[id] = 1; }
        this.lsx[id] = px; this.lsy[id] = py; this.stillT[id] = 0;
        this.swx[id] = 0; this.swy[id] = 0; this.swh[id] = 0;
        continue;
      }

      // Wildlife + apex: a single light inertial chase + smooth facing (no caste,
      // no idle behaviours — they are not colony members). Reuses the same
      // smooth-damp / turn helpers and per-id buffers as organisms.
      if (kind !== KIND.Organism) {
        const wm = WILDLIFE_MOTION;
        this.rx[id] = smoothDamp(this.rx[id] as number, px, this.vx, id, wm.pos, dt);
        this.ry[id] = smoothDamp(this.ry[id] as number, py, this.vy, id, wm.pos, dt);
        const wvx = this.vx[id] as number;
        const wvy = this.vy[id] as number;
        if (wvx * wvx + wvy * wvy > HEAD_MIN_SPEED2) {
          this.head[id] = turnToward(this.head[id] as number, headingFromVel(wvx, wvy), wm.turn, dt);
          this.hasHead[id] = 1;
        }
        continue;
      }

      // Inertial chase: per-caste critically-damped spring → acceleration into a
      // gap + arrival deceleration. With the heavier per-caste smoothing times this
      // also PATH-SMOOTHS — the body flows through the greedy pather's tile
      // staircase as a curve instead of stepping corner to corner.
      const m = CASTE_MOTION[e.caste[id] as number] ?? DEFAULT_MOTION;
      this.rx[id] = smoothDamp(this.rx[id] as number, px, this.vx, id, m.pos, dt);
      this.ry[id] = smoothDamp(this.ry[id] as number, py, this.vy, id, m.pos, dt);

      // Idle detection by TIME (robust to the fps : tps ratio): has the sim tile
      // moved? If it's held still past IDLE_DELAY the organism is waiting/working.
      if (px !== (this.lsx[id] as number) || py !== (this.lsy[id] as number)) {
        this.lsx[id] = px; this.lsy[id] = py; this.stillT[id] = 0;
      } else {
        this.stillT[id] = (this.stillT[id] as number) + dt;
      }
      const idle = (this.stillT[id] as number) > IDLE_DELAY;

      const vxi = this.vx[id] as number;
      const vyi = this.vy[id] as number;
      const moving = vxi * vxi + vyi * vyi > HEAD_MIN_SPEED2;
      const ease = 1 - Math.exp(-dt * SWAY_EASE);

      if (idle) {
        // Idle behaviours (render-only): face the goal, sway gently, occasionally
        // groom — so a resting colony looks alive + readable, never frozen.
        this.idleT[id] = (this.idleT[id] as number) + dt;
        const phase = (this.idleT[id] as number) + id * 0.7;
        // 1) ORIENT toward the current sim target (look where you intend to go).
        const dx = (e.targetX[id] as number) - px;
        const dy = (e.targetY[id] as number) - py;
        if (dx !== 0 || dy !== 0) {
          this.head[id] = turnToward(this.head[id] as number, headingFromVel(dx, dy), ORIENT_TURN, dt);
          this.hasHead[id] = 1;
        }
        // 2) SWAY — a slow breathing orbit around the tile (per-caste amplitude).
        const tsx = Math.sin(phase * SWAY_FREQ) * m.sway;
        const tsy = Math.cos(phase * SWAY_FREQ * 0.8) * m.sway;
        this.swx[id] = (this.swx[id] as number) + (tsx - (this.swx[id] as number)) * ease;
        this.swy[id] = (this.swy[id] as number) + (tsy - (this.swy[id] as number)) * ease;
        // 3) GROOM — brief enveloped head-wiggle bouts (busy Workers fidget most).
        const tgh = groomOffset(phase, m.sway * GROOM_AMP);
        this.swh[id] = (this.swh[id] as number) + (tgh - (this.swh[id] as number)) * ease;
      } else {
        // Moving (or just-stopped): face travel, and ease the idle offsets back out
        // so there is no pop when motion resumes.
        if (moving) {
          this.head[id] = turnToward(this.head[id] as number, headingFromVel(vxi, vyi), m.turn, dt);
          this.hasHead[id] = 1;
        }
        this.swx[id] = (this.swx[id] as number) * (1 - ease);
        this.swy[id] = (this.swy[id] as number) * (1 - ease);
        this.swh[id] = (this.swh[id] as number) * (1 - ease);
      }
    }
  }

  private driveCamera(session: GameSession, dt: number, gridW: number, gridH: number): void {
    let zoomT = 1;
    let cxT = gridW / 2;
    let cyT = gridH / 2;

    if (this.mode === 'manual') {
      // Re-clamp the player's target against the CURRENT canvas/grid (handles
      // resize + zoom-out) and ease toward it. cssW/cssH come from the last
      // viewbox call; fall back to the canvas-independent grid extents if unset.
      const cw = this.lastCssW > 0 ? this.lastCssW : gridW;
      const ch = this.lastCssH > 0 ? this.lastCssH : gridH;
      const fit = baseFitCell(cw, ch, gridW, gridH);
      this.manZoom = clampZoom(this.manZoom);
      const c = clampCamera(this.manCx, this.manCy, this.manZoom, cw, ch, gridW, gridH, fit);
      this.manCx = c.cx; this.manCy = c.cy;
      zoomT = this.manZoom; cxT = this.manCx; cyT = this.manCy;
    } else if (this.mode === 'follow') {
      zoomT = FOLLOW_ZOOM;
      cxT = session.world.colonies.homeXAt(0) + 0.5;
      cyT = session.world.colonies.homeYAt(0) + 0.5;
    } else if (this.mode === 'cinematic') {
      this.tourTime += dt;
      if (this.tourTime >= DWELL) { this.tourTime = 0; this.tourIdx++; }
      const t = this.cinematicTarget(session, gridW, gridH);
      zoomT = t.zoom; cxT = t.cx; cyT = t.cy;
    }

    if (!this.inited) { this.curZoom = zoomT; this.curCx = cxT; this.curCy = cyT; this.inited = true; }
    const k = 1 - Math.exp(-dt * CAM_SPEED);
    this.curZoom += (zoomT - this.curZoom) * k;
    this.curCx += (cxT - this.curCx) * k;
    this.curCy += (cyT - this.curCy) * k;
  }

  /** Cinematic points of interest: overview, then each living colony's nest. */
  private cinematicTarget(session: GameSession, gridW: number, gridH: number): { zoom: number; cx: number; cy: number } {
    const colonies = session.world.colonies;
    const nests: { cx: number; cy: number }[] = [];
    for (let cid = 0; cid < colonies.count; cid++) {
      if (colonies.populationAt(cid) > 0) nests.push({ cx: colonies.homeXAt(cid) + 0.5, cy: colonies.homeYAt(cid) + 0.5 });
    }
    const stops = nests.length + 1; // +1 overview
    const idx = this.tourIdx % stops;
    if (idx === 0) return { zoom: 1, cx: gridW / 2, cy: gridH / 2 };
    const nest = nests[idx - 1] as { cx: number; cy: number };
    return { zoom: CINE_ZOOM, cx: nest.cx, cy: nest.cy };
  }

  // --- queries for render ----------------------------------------------------

  /** ViewBox from the current smoothed camera (offsets are sub-pixel for smooth pan). */
  viewbox(cssW: number, cssH: number, gridW: number, gridH: number): ViewBox {
    this.lastCssW = cssW;
    this.lastCssH = cssH;
    const baseCell = baseFitCell(cssW, cssH, gridW, gridH);
    const cell = Math.max(2, baseCell * this.curZoom);
    const offX = cssW / 2 - this.curCx * cell;
    const offY = cssH / 2 - this.curCy * cell;
    return { cell, offX, offY };
  }

  /** Interpolated tile X for organism `id` (smoothed base + idle sway; falls back to the raw sim X). */
  ox(id: number, fallback: number): number {
    return this.hasPos[id] === 1 ? (this.rx[id] as number) + (this.swx[id] as number) : fallback;
  }
  oy(id: number, fallback: number): number {
    return this.hasPos[id] === 1 ? (this.ry[id] as number) + (this.swy[id] as number) : fallback;
  }

  /**
   * Smoothed facing angle (radians, canvas convention: 0 = nose up) for organism
   * `id`, for the painter to rotate the body — the inertial heading plus any idle
   * groom-wiggle. 0 until the organism first moves.
   */
  heading(id: number): number {
    return this.hasHead[id] === 1 ? (this.head[id] as number) + (this.swh[id] as number) : 0;
  }
}
